import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import { Button, Field } from './ui';
import { useToast } from './Toast';

const RESEND_COOLDOWN = 30; // seconds

/**
 * The "enter the code we emailed you" step, shared by Register (right after
 * signing up) and Login (when signing in to an account that never finished
 * verifying). Calling code only needs to hand it an email and take over once
 * verified — the code itself, resend cooldown and errors all live here.
 */
export default function OtpVerify({ email, onVerified, lede }) {
  const { t } = useTranslation();
  const toast = useToast();
  const { verifyEmail, resendCode } = useAuth();
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [cooldown, setCooldown] = useState(RESEND_COOLDOWN);

  useEffect(() => {
    if (cooldown <= 0) return undefined;
    const timer = setInterval(() => setCooldown((c) => Math.max(0, c - 1)), 1000);
    return () => clearInterval(timer);
  }, [cooldown]);

  async function submit(event) {
    event.preventDefault();
    setBusy(true);
    try {
      const user = await verifyEmail(email, code);
      onVerified(user);
    } catch (err) {
      toast.error(err.message);
      setCode('');
    } finally {
      setBusy(false);
    }
  }

  async function resend() {
    setCooldown(RESEND_COOLDOWN);
    try {
      await resendCode(email);
      toast.success(t('otp.newCodeSent'));
    } catch (err) {
      toast.error(err.message);
    }
  }

  return (
    <>
      <h1>{t('otp.title')}</h1>
      <p className="muted">
        {lede || t('otp.ledeDefault', { email })}
      </p>

      <form onSubmit={submit} noValidate>
        <Field label={t('otp.codeLabel')} required hint={t('otp.codeHint')}>
          <input
            value={code}
            onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
            inputMode="numeric"
            autoComplete="one-time-code"
            maxLength={6}
            placeholder="123456"
            autoFocus
            required
          />
        </Field>
        <Button type="submit" loading={busy} disabled={code.length !== 6}>{t('otp.verify')}</Button>
      </form>

      <p className="auth-alt">
        {t('otp.noCode')}{' '}
        <button
          type="button"
          className="link"
          disabled={cooldown > 0}
          onClick={resend}
          style={{ opacity: cooldown > 0 ? 0.6 : 1 }}
        >
          {cooldown > 0 ? t('otp.resendCooldown', { seconds: cooldown }) : t('otp.resend')}
        </button>
      </p>
    </>
  );
}
